import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import TeacherAcademicHeader from "./TeacherAcademicHeader";

function TeacherHomework({ courses = [], loading: parentLoading }) {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const location = useLocation();

  const lockedCourse =
    location.state?.course ||
    courses.find((c) => String(c.id) === String(assignmentId)) ||
    null;

  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseId, setCourseId] = useState(
    lockedCourse ? String(lockedCourse.course_id || lockedCourse.id || "") : "",
  );
  const [classId, setClassId] = useState(
    lockedCourse ? String(lockedCourse.class_id || "") : "",
  );
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [targetMode, setTargetMode] = useState("class");
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!lockedCourse) return;
    setCourseId(String(lockedCourse.course_id || lockedCourse.id || ""));
    setClassId(String(lockedCourse.class_id || ""));
  }, [lockedCourse]);

  const loadList = async () => {
    const params = new URLSearchParams();
    if (courseId) params.set("course_id", courseId);
    if (classId) params.set("class_id", classId);
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/homework?${params}`, {
      headers: getAuthHeaders(),
    });
    const data = res.ok ? await res.json() : [];
    setItems(Array.isArray(data) ? data : []);
  };

  const loadStudents = async (cls) => {
    if (!cls) {
      setStudents([]);
      return;
    }
    const params = new URLSearchParams({ class_id: cls });
    if (courseId) params.set("course_id", courseId);
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/students?${params}`, {
      headers: getAuthHeaders(),
    });
    const data = res.ok ? await res.json() : [];
    setStudents(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        await loadList();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [courseId, classId]);

  useEffect(() => {
    loadStudents(classId);
    setSelectedStudents([]);
  }, [classId, courseId]);

  const toggleStudent = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !courseId || !classId) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("course_id", courseId);
      fd.append("class_id", classId);
      if (description.trim()) fd.append("description", description.trim());
      if (dueDate) fd.append("due_date", dueDate);
      fd.append("target_mode", targetMode);
      if (targetMode === "selected") {
        fd.append("student_ids", selectedStudents.join(","));
      }
      if (file) fd.append("file", file);

      const res = await fetch(`${API_BASE_URL}/api/teachers/me/homework`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create homework");
      }
      setTitle("");
      setDescription("");
      setDueDate("");
      setFile(null);
      setSelectedStudents([]);
      setTargetMode("class");
      await loadList();
    } catch (err) {
      alert(err.message || "Failed to create homework");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this homework?")) return;
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/homework/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (res.ok) loadList();
  };

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading homework…</p>
      </div>
    );
  }

  return (
    <div className="tp-academic-shell">
      <TeacherAcademicHeader
        title="Homework"
        subtitle={
          lockedCourse
            ? `${lockedCourse.course_name || lockedCourse.name} · ${lockedCourse.class_name || "Class"}`
            : "Assign homework by course and class."
        }
        backLabel="Back to course"
        onBack={
          assignmentId
            ? () => navigate(`/teacher/courses/${assignmentId}`, { state: { course: lockedCourse } })
            : null
        }
      />

      <div className="tp-academic-panel">
        <div className="tp-academic-panel__head">
          <h2>Assign homework</h2>
          <p className="tp-muted">Whole class or selected students.</p>
        </div>
        <form onSubmit={submit} className="tp-form-grid tp-form-grid--academic">
          <label>
            Title
            <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Homework title" />
          </label>
          <label>
            Due date
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <label className="tp-form-full">
            Description
            <textarea
              className="tp-textarea tp-textarea--lg"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Instructions, topics covered, submission format…"
            />
          </label>
          <label>
            Assign to
            <select value={targetMode} onChange={(e) => setTargetMode(e.target.value)}>
              <option value="class">Entire class (bulk)</option>
              <option value="selected">Selected students</option>
            </select>
          </label>
          <label>
            Attachment (optional)
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          {targetMode === "selected" && (
            <div className="tp-form-full">
              <span className="tp-field-label">Select students</span>
              <div className="tp-student-checks tp-student-checks--boxed">
                {students.map((s) => (
                  <label key={s.id}>
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(s.id)}
                      onChange={() => toggleStudent(s.id)}
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="tp-form-full tp-form-actions">
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? "Assigning…" : "Assign Homework"}
            </button>
          </div>
        </form>
      </div>

      <div className="tp-academic-panel" style={{ marginTop: "1rem" }}>
        <div className="tp-academic-panel__head">
          <h2>Assigned homework</h2>
          <span className="tp-badge">{items.length} items</span>
        </div>
        {items.length === 0 ? (
          <p className="tp-muted">No homework assigned yet.</p>
        ) : (
          <div className="tp-table-wrap tp-table-wrap--bordered">
            <table className="tp-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Target</th>
                  <th>Due</th>
                  <th>File</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((hw) => (
                  <tr
                    key={hw.id}
                    className="tp-row-clickable"
                    onClick={() =>
                      navigate(`/teacher/courses/${assignmentId}/homework/${hw.id}`, {
                        state: { course: lockedCourse, homework: hw },
                      })
                    }
                  >
                    <td>
                      <strong>{hw.title}</strong>
                      {hw.description && <div className="tp-muted">{hw.description}</div>}
                    </td>
                    <td>
                      {hw.target_mode === "selected"
                        ? `${(hw.student_ids || []).length} students`
                        : "Whole class"}
                    </td>
                    <td>{hw.due_date || "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {hw.attachment_path ? (
                        <a href={`${API_BASE_URL}${hw.attachment_path}`} target="_blank" rel="noreferrer">
                          {hw.attachment_name || "Download"}
                        </a>
                      ) : "—"}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => remove(hw.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherHomework;
